import {
  JournalPostsResponse,
  PagesResponse,
  PricingResponse,
  ShootsResponse,
} from '@diana/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpenText,
  Camera,
  ChevronRight,
  ExternalLink,
  FileText,
  Images,
  LogOut,
  Settings,
  Tag,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAdminApi } from './AdminApiContext.js';
import { PublicationEntry } from './PublicationEntry.js';
import { useShellPublishing } from './ShellPublishingContext.js';

export function SiteHub() {
  const api = useAdminApi();
  const { user, pendingCount, pendingLoading, publishing, openPublish, logout } =
    useShellPublishing();
  const pages = useQuery({
    queryKey: ['pages'],
    queryFn: () => api.get('/api/v1/admin/pages', PagesResponse),
  });
  const pricing = useQuery({
    queryKey: ['pricing'],
    queryFn: () => api.get('/api/v1/admin/pricing', PricingResponse),
  });
  const shoots = useQuery({
    queryKey: ['shoots'],
    queryFn: () => api.get('/api/v1/admin/shoots', ShootsResponse),
  });
  const journal = useQuery({
    queryKey: ['journal'],
    queryFn: () => api.get('/api/v1/admin/journal', JournalPostsResponse),
  });
  const sections = [
    {
      label: 'Packages',
      href: '/packages',
      icon: Tag,
      count: pricing.data?.pricing.plans.length,
      tint: 'blue',
    },
    {
      label: 'Galleries',
      href: '/shoots',
      icon: Camera,
      count: shoots.data?.shoots.length,
      tint: 'purple',
    },
    {
      label: 'Journal posts',
      href: '/journal',
      icon: BookOpenText,
      count: journal.data?.journal.length,
      tint: 'green',
    },
    { label: 'Media', href: '/settings/media', icon: Images, count: undefined, tint: 'orange' },
  ];
  return (
    <article className="site-hub">
      <header className="site-hub__heading">
        <h1>Site</h1>
        <a href="https://perova.photo/" target="_blank" rel="noreferrer">
          View site
          <ExternalLink size={15} aria-hidden="true" />
        </a>
      </header>
      <PublicationEntry
        pendingCount={pendingCount}
        pendingLoading={pendingLoading}
        publishing={publishing}
        onOpen={openPublish}
      />
      <section className="site-hub__pages" aria-labelledby="site-pages-heading">
        <header>
          <h2 id="site-pages-heading">Pages</h2>
          <Link to="/pages">
            All pages
            <ChevronRight size={15} aria-hidden="true" />
          </Link>
        </header>
        {pages.isPending ? (
          <div className="editor-skeleton" aria-label="Loading pages">
            <span />
            <span />
            <span />
          </div>
        ) : pages.isError ? (
          <div className="site-hub__error" role="alert">
            <p>Pages could not be loaded</p>
            <button
              className="button button--quiet"
              type="button"
              onClick={() => void pages.refetch()}
            >
              Try again
            </button>
          </div>
        ) : pages.data.pages.length === 0 ? (
          <p>No pages yet.</p>
        ) : (
          pages.data.pages.map((page) => (
            <Link className="site-hub__page" key={page.id} to={`/pages/${page.id}`}>
              <span className="site-hub__page-icon">
                <FileText size={21} aria-hidden="true" />
              </span>
              <span className="site-hub__page-copy">
                <strong>
                  {page.slug === 'home'
                    ? 'Homepage'
                    : page.slug
                        .split('-')
                        .map((word) => word[0]?.toUpperCase() + word.slice(1))
                        .join(' ')}
                </strong>
                <small>{page.route}</small>
              </span>
              <span className="site-hub__visibility" data-public={page.siteAccess === 'Public'}>
                {page.siteAccess === 'Public' ? 'Public' : 'Hidden'}
              </span>
            </Link>
          ))
        )}
      </section>
      <section aria-labelledby="site-content-heading">
        <h2 id="site-content-heading">Content</h2>
        <div className="site-hub__content">
          {sections.map(({ label, href, icon: Icon, count, tint }) => (
            <Link key={href} to={href} className="site-hub__tile" data-tint={tint}>
              <span>{count === undefined ? <Icon size={25} aria-hidden="true" /> : count}</span>
              <strong>{label}</strong>
            </Link>
          ))}
        </div>
      </section>
      <Link className="site-hub__settings" to="/settings">
        <Settings size={20} aria-hidden="true" />
        <span>
          <strong>Settings</strong>
          <small>Contacts, people, Mac app</small>
        </span>
        <ChevronRight size={18} aria-hidden="true" />
      </Link>
      <div className="site-hub__account">
        <span className="admin-identity__initial">{user.email.slice(0, 1).toUpperCase()}</span>
        <span>
          <strong>{user.email}</strong>
          <small>{user.role}</small>
        </span>
        <button type="button" aria-label="Sign out" onClick={logout}>
          <LogOut size={20} aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}
